import SignInOverlay from "@/components/SignInOverlay";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  LineChart,
  TrendingUp,
  FileText,
  Calculator,
  GraduationCap,
  Search,
  ChevronRight,
  User,
  ChevronDown,
  LogOut,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AuthModal from "../components/AuthModal";

const StartInvesting = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user_data") || localStorage.getItem("user");
    const userToken = localStorage.getItem("user_token") || localStorage.getItem("token");
    const sessionToken = sessionStorage.getItem("user_token");
    const sessionUser = sessionStorage.getItem("user_data");
    
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setIsLoggedIn(true);
      } catch (e) {
        console.error("Failed to parse user data");
      }
    } else if (sessionUser) {
      try {
        setUser(JSON.parse(sessionUser));
        setIsLoggedIn(true);
      } catch (e) {
        console.error("Failed to parse session user data");
      }
    }
    
    // Also check if token exists
    if (userToken || sessionToken) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData.user);
    setIsLoggedIn(true);
    setShowAuth(false);
    localStorage.setItem("user_token", userData.token || "authenticated");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user_data");
    setUser(null);
    setIsLoggedIn(false);
    setShowProfileMenu(false);
  };

  const tools = [
    {
      icon: LineChart,
      title: "Stock Market Simulator",
      description: "Practice trading with $10K virtual cash. Real prices, zero risk.",
      link: "/tools?tool=simulator&from=start-investing",
      action: "Start Trading",
      popular: true
    },
    {
      icon: BarChart3,
      title: "Daily Market Update",
      description: "AI-powered market summaries with TSX and S&P 500 insights",
      link: "/tools?tool=market-update&from=start-investing",
      action: "View Today's Update"
    },
    {
      icon: FileText,
      title: "Document Analyzer",
      description: "Upload any 10-K or earnings report for instant AI analysis",
      link: "/tools?tool=document-analyzer&from=start-investing",
      action: "Analyze Document"
    },
    {
      icon: Calculator,
      title: "Compound Interest Calculator",
      description: "See how your money can grow over time with compound returns",
      link: "/tools?tool=compound&from=start-investing",
      action: "Calculate Growth"
    },
    {
      icon: GraduationCap,
      title: "Learn Hub",
      description: "105+ financial terms with AI-powered explanations",
      link: "/tools?tool=learnhub&from=start-investing",
      action: "Start Learning"
    },
    {
      icon: Search,
      title: "AI Stock Search",
      description: "Find investments using natural language queries",
      link: "/tools?tool=simulator&from=start-investing",
      action: "Search Stocks"
    }
  ];

  return (
    <SignInOverlay message="Sign in to access investing guides and tools">
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="pt-24 pb-12 lg:pt-32 lg:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
              ← Back to Home
            </Link>
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              📈 Start Investing
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-navy-700 mb-6 leading-tight">
              Begin Your
              <span className="text-emerald-500"> Investment Journey</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              Learn to invest with confidence. Practice with virtual money, 
              analyze real markets, and build the skills you need to grow your wealth.
            </p>
            <Link to="/tools?tool=simulator&from=start-investing">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg">
                <LineChart className="w-5 h-5 mr-2" />
                Open Stock Simulator
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-3">
              Investing Tools
            </h2>
            <p className="text-slate-600">
              Everything you need to start your investment journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link to={tool.link}>
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer group h-full">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                      <tool.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-navy-700">{tool.title}</h3>
                      {tool.popular && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Popular</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-3">{tool.description}</p>
                    <span className="text-emerald-600 font-medium text-sm group-hover:underline">
                      {tool.action} →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-navy-700 to-navy-800 rounded-3xl p-8 lg:p-12">
            <span className="text-4xl mb-4 block">🎯</span>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
              Not Sure Where to Start?
            </h2>
            <p className="text-slate-300 mb-6 max-w-lg mx-auto">
              Take our quick quiz to find the right tools for your financial goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl">
                  Take the Quiz
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/tools">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl">
                  Browse All Tools
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />
    </div>
    </SignInOverlay>
  );
};

export default StartInvesting;
